import {Component, OnDestroy, OnInit} from '@angular/core';
import {select, Store} from '@ngrx/store';
import * as fromRoot from '../../../reducers';
import * as fromAfbApi from '../../../afb-api/reducers';
import * as fromFloorPlanEditor from '../../reducers/index';
import {Observable, Subscription} from 'rxjs';
import {filter, skip, withLatestFrom} from 'rxjs/operators';
import {
  RemoveAllDeviceMarker,
  RemoveDeviceMarker,
  ResetInitialEntitiesToCurrent,
  SetDeviceMarker
} from '../../actions/floor-plan-editor-device-marker.actions';
import {DeviceMarker} from '../../../floor-plan-shared/models/device-marker';
import {OrganizationWithToken} from '../../../core/models/core';
import {ImageWithSource} from '../../models/floor-plan-editor-view';
import {ResetFloorImageChangeStatus, UploadFloorImage} from '../../actions/floor-plan-editor-view.actions';
import {GetOrganizationDevices} from '../../../afb-api/actions/device.actions';
import {Device} from '../../../afb-api/models/device';

@Component({
  selector: 'awair-floor-plan-editor-main',
  templateUrl: './floor-plan-editor-main.component.html',
  styleUrls: ['./floor-plan-editor-main.component.scss']
})

export class FloorPlanEditorMainComponent implements OnInit, OnDestroy {

  selectedOrganization$: Observable<OrganizationWithToken> = this.rootStore.pipe(
    select(fromRoot.selectedOrganization),
    filter((selectedOrganization: OrganizationWithToken) => selectedOrganization != null)
  );
  selectedFloorId$: Observable<string> = this.floorPlanEditorStore.pipe(
    select(fromFloorPlanEditor.selectedFloorId)
  );
  uploadedFloorImageWithSource$: Observable<ImageWithSource> = this.floorPlanEditorStore.pipe(
    select(fromFloorPlanEditor.uploadedImageWithSource)
  );
  selectedFloorPlanImageUrl$: Observable<string> = this.floorPlanEditorStore.pipe(
    select(fromFloorPlanEditor.selectedFloorPlanImageUrl)
  );
  postFloorImageError$: Observable<string> = this.afbApiStore.pipe(
    select(fromAfbApi.postFloorPlanImageError),
    skip(1)
  );
  hasFloor$: Observable<boolean> = this.afbApiStore.pipe(
    select(fromAfbApi.hasFloor)
  );
  deviceEntities$: Observable<{[deviceUuid: string]: Device}> = this.afbApiStore.pipe(
    select(fromAfbApi.deviceEntities),
    filter((entities: {[deviceUuid: string]: Device}) => entities != null)
  );

  // Editor Map
  deviceMarkerEntities$: Observable<{[deviceUuid: string]: DeviceMarker}> = this.floorPlanEditorStore.pipe(
    select(fromFloorPlanEditor.deviceMarkerEntities)
  );
  addedDeviceMarkers$: Observable<DeviceMarker[]> = this.floorPlanEditorStore.pipe(
    select(fromFloorPlanEditor.addedDeviceMarkers),
    filter((deviceMarkers: DeviceMarker[]) => deviceMarkers != null)
  );
  updatedDeviceMarker$: Observable<DeviceMarker> = this.floorPlanEditorStore.pipe(
    select(fromFloorPlanEditor.updatedDeviceMarker),
    filter((deviceMarker: DeviceMarker) => deviceMarker != null)
  );
  removedDeviceMarkerUuids$: Observable<string[]> = this.floorPlanEditorStore.pipe(
    select(fromFloorPlanEditor.removedDeviceMarkerUuids),
    filter((deviceMarkerUuids: string[]) => deviceMarkerUuids != null)
  );
  saveFloorPlanLoaded$: Observable<[boolean, OrganizationWithToken]> = this.floorPlanEditorStore.pipe(
    select(fromFloorPlanEditor.saveFloorPlanLoaded),
    withLatestFrom(this.selectedOrganization$),
    skip(1)
  );
  hasDeviceMarkers$: Observable<boolean> = this.floorPlanEditorStore.pipe(
    select(fromFloorPlanEditor.hasDeviceMarkers)
  );

  hasDeviceMarkers: boolean = false;
  saveToastMsg: string = '';
  showToast: boolean = false;

  private isFloorPlanSavedSubscription: Subscription;
  private hasDeviceMarkersSubscription: Subscription;
  private postFloorPlanImageSubscription: Subscription;

  constructor(private rootStore: Store<fromRoot.State>,
              private afbApiStore: Store<fromAfbApi.State>,
              private floorPlanEditorStore: Store<fromFloorPlanEditor.State>) {
  }

  ngOnInit() {
    this.isFloorPlanSavedSubscription = this.saveFloorPlanLoaded$.subscribe(
      ([isFloorPlanSaved, selectedOrganization]: [boolean, OrganizationWithToken]) => {
        if (isFloorPlanSaved) {
          // Reset initial device marker entities to current entities to reset diff
          this.floorPlanEditorStore.dispatch(new ResetInitialEntitiesToCurrent());
          // Clear out the uploaded image state
          this.floorPlanEditorStore.dispatch(new ResetFloorImageChangeStatus());
          // Request devices after floor plan is saved in order to update floor plan status on devices
          this.afbApiStore.dispatch(new GetOrganizationDevices({
            organizationToken: selectedOrganization.token
          }));
          this.saveToastMsg = 'Successfully saved!';
          this.showToast = true;
          setTimeout(() => {
            this.showToast = false;
          }, 2500);
        }
      });
    this.hasDeviceMarkersSubscription = this.hasDeviceMarkers$.subscribe((hasDeviceMarkers: boolean) => {
      this.hasDeviceMarkers = hasDeviceMarkers;
    });
    this.postFloorPlanImageSubscription = this.postFloorImageError$.subscribe((errorMessage: string) => {
      if (errorMessage && errorMessage.length > 0) {
        alert(errorMessage);
      }
    });
  }

  ngOnDestroy() {
    if (this.isFloorPlanSavedSubscription) {
      this.isFloorPlanSavedSubscription.unsubscribe();
    }
    if (this.hasDeviceMarkersSubscription) {
      this.hasDeviceMarkersSubscription.unsubscribe();
    }
    if (this.postFloorPlanImageSubscription) {
      this.postFloorPlanImageSubscription.unsubscribe();
    }
  }

  onFileUpload(imageWithSource: ImageWithSource, hasDeviceMarkers: boolean): void {
    if (hasDeviceMarkers) {
      this.floorPlanEditorStore.dispatch(new RemoveAllDeviceMarker());
    }
    this.floorPlanEditorStore.dispatch(new UploadFloorImage(imageWithSource));
  }

  onDropDevice({relativeXPercentage, relativeYPercentage, data}: { relativeXPercentage: number, relativeYPercentage: number, data?: any },
               deviceEntities: {[deviceUuid: string]: Device},
               deviceMarkerEntities: {[deviceUuid: string]: DeviceMarker},
               selectedFloorId: string): void {
    const droppedDevice: Device = data && data.deviceUuid && deviceEntities[data.deviceUuid];
    const droppedDeviceFloorId: string = droppedDevice && droppedDevice.floorId;
    const droppedDeviceFloorName: string = droppedDevice && droppedDevice.floorName;
    const isInDeviceMarkerEntities: boolean = data && data.deviceUuid && deviceMarkerEntities[data.deviceUuid] != null;

    if (droppedDeviceFloorId == null || isInDeviceMarkerEntities || droppedDeviceFloorId === selectedFloorId ||
      droppedDeviceFloorId && confirm(`This device is already placed in ${droppedDeviceFloorName}.\nDo you want to move the device?`)) {

      this.floorPlanEditorStore.dispatch(new SetDeviceMarker({
        deviceUuid: data && data.deviceUuid != null ? data.deviceUuid : null,
        name: data && data.deviceName != null ? data.deviceName : null,
        x: relativeXPercentage,
        y: relativeYPercentage
      }));
    }
  }

  onRemoveMarker(deviceUuid: string): void {
    this.floorPlanEditorStore.dispatch(new RemoveDeviceMarker({deviceUuid}));
  }
}
